import React, { useState } from 'react';
import {
  Row, Col, Card, Tabs, List, Badge, Calendar, Tag, Avatar, 
  Statistic, Progress, Button, Input, Dropdown, Menu, Empty, Tooltip
} from 'antd';
import {
  BellOutlined, MessageOutlined, FileDoneOutlined, CheckCircleOutlined,
  ClockCircleOutlined, StarOutlined, StarFilled, SearchOutlined,
  MoreOutlined, FileTextOutlined, TeamOutlined, BookOutlined,
  DashboardOutlined, SolutionOutlined, FilterOutlined, PlusOutlined
} from '@ant-design/icons';
import './index.css';

const { TabPane } = Tabs;

// 模拟数据
const todoListData = [
  {
    id: 1,
    title: '审批工程建设项目申请',
    type: '任务审批',
    deadline: '2023-10-26 17:00',
    priority: 'high',
    completed: false,
    department: '行政审批局'
  },
  {
    id: 2,
    title: '回复企业服务工单',
    type: '工单处理',
    deadline: '2023-10-25 14:00',
    priority: 'medium',
    completed: false,
    department: '营商环境办'
  },
  {
    id: 3,
    title: '数据质量审核',
    type: '数据工作',
    deadline: '2023-10-27 12:00',
    priority: 'medium',
    completed: false,
    department: '数据管理科'
  },
  {
    id: 4,
    title: '营商环境日报审核',
    type: '日常工作',
    deadline: '2023-10-25 10:00',
    priority: 'high',
    completed: true,
    department: '办公室'
  },
  {
    id: 5,
    title: '参加部门周会',
    type: '会议',
    deadline: '2023-10-27 09:30',
    priority: 'low',
    completed: false,
    department: '办公室'
  },
];

const messageData = [
  {
    id: 1,
    title: '企业服务工单回复提醒',
    content: '您有一个新的工单回复，请及时查看。',
    sender: '工单系统',
    time: '2023-10-25 09:24',
    read: false,
    type: 'notification'
  },
  {
    id: 2,
    title: '预警指标异常',
    content: '城东区企业开办时间指标出现异常，已触发预警。',
    sender: '预警系统',
    time: '2023-10-24 16:35',
    read: true,
    type: 'alert'
  },
  {
    id: 3,
    title: '工单处理期限提醒',
    content: '您有3个工单即将到期，请及时处理。',
    sender: '工单系统',
    time: '2023-10-24 14:20',
    read: false,
    type: 'reminder'
  },
  {
    id: 4,
    title: '关于开展营商环境提升行动的通知',
    content: '请各部门于10月28日前提交营商环境提升行动计划...',
    sender: '营商环境办',
    time: '2023-10-23 11:05',
    read: true,
    type: 'announcement'
  },
  {
    id: 5,
    title: '系统升级通知',
    content: '系统将于2023年10月28日晚进行升级维护，维护时间为22:00-24:00...',
    sender: '系统管理员',
    time: '2023-10-20 15:30',
    read: true,
    type: 'system'
  },
];

const collectionsData = [
  {
    id: 1,
    title: '城东区营商环境指标监测报告',
    type: 'report',
    createTime: '2023-10-20',
    creator: '张三',
    starred: true,
    tags: ['报告', '指标监测', '城东区']
  },
  {
    id: 2,
    title: '企业开办时长数据分析图表',
    type: 'chart',
    createTime: '2023-10-15',
    creator: '李四',
    starred: true,
    tags: ['图表', '企业开办', '数据分析']
  },
  {
    id: 3,
    title: '工程建设项目审批流程优化方案',
    type: 'document',
    createTime: '2023-10-10',
    creator: '王五',
    starred: true,
    tags: ['方案', '工程建设', '流程优化']
  },
  {
    id: 4,
    title: '营商环境季度评估模型',
    type: 'model',
    createTime: '2023-09-28',
    creator: '赵六',
    starred: false,
    tags: ['模型', '评估', '季度']
  },
  {
    id: 5,
    title: '企业满意度调查问卷',
    type: 'form',
    createTime: '2023-09-15',
    creator: '张三',
    starred: false,
    tags: ['问卷', '满意度', '企业']
  },
];

const schedulesData = [
  {
    id: 1,
    title: '部门周会',
    time: '2023-10-27 09:30-10:30',
    location: '3楼会议室',
    participants: ['张三', '李四', '王五', '赵六'],
    type: '例会'
  },
  {
    id: 2,
    title: '企业走访调研',
    time: '2023-10-28 14:00-16:00',
    location: '科技园区',
    participants: ['张三', '李四'],
    type: '外勤'
  },
  {
    id: 3,
    title: '营商环境提升行动部署会',
    time: '2023-10-30 10:00-11:30',
    location: '5楼大会议室',
    participants: ['张三', '李四', '王五', '赵六', '钱七', '孙八'],
    type: '专题会'
  }
];

const Workbench = () => {
  const [todoActiveKey, setTodoActiveKey] = useState('pending');
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  // 筛选待办事项
  const getFilteredTodos = () => {
    if (todoActiveKey === 'all') {
      return todoListData;
    } else if (todoActiveKey === 'pending') {
      return todoListData.filter(todo => !todo.completed);
    } else if (todoActiveKey === 'completed') {
      return todoListData.filter(todo => todo.completed);
    }
    return todoListData;
  };
  
  // 获取待办事项优先级标签
  const getPriorityTag = (priority) => {
    switch(priority) {
      case 'high':
        return <Tag color="red">紧急</Tag>;
      case 'medium':
        return <Tag color="orange">重要</Tag>;
      case 'low':
        return <Tag color="blue">普通</Tag>;
      default:
        return null;
    }
  };
  
  // 获取消息类型标签
  const getMessageTypeTag = (type) => {
    switch(type) {
      case 'notification':
        return <Tag color="blue">通知</Tag>;
      case 'alert':
        return <Tag color="red">预警</Tag>;
      case 'reminder':
        return <Tag color="orange">提醒</Tag>;
      case 'announcement':
        return <Tag color="green">公告</Tag>;
      case 'system':
        return <Tag color="purple">系统</Tag>;
      default:
        return null;
    }
  };
  
  // 获取收藏项目图标
  const getCollectionTypeIcon = (type) => {
    switch(type) {
      case 'report':
        return <FileTextOutlined />;
      case 'chart':
        return <DashboardOutlined />;
      case 'document':
        return <FileTextOutlined />;
      case 'model':
        return <SolutionOutlined />;
      case 'form':
        return <FileTextOutlined />;
      default:
        return <FileTextOutlined />;
    }
  };
  
  // 日历单元格渲染
  const dateCellRender = (value) => {
    const day = value.date();
    const month = value.month();
    const year = value.year();
    
    // 检查是否有待办
    const hasTodo = todoListData.some(todo => {
      const deadlineDate = new Date(todo.deadline);
      return deadlineDate.getDate() === day && 
             deadlineDate.getMonth() === month && 
             deadlineDate.getFullYear() === year;
    });
    
    // 检查是否有日程
    const hasSchedule = schedulesData.some(schedule => {
      const scheduleDate = new Date(schedule.time.split(' ')[0]);
      return scheduleDate.getDate() === day && 
             scheduleDate.getMonth() === month && 
             scheduleDate.getFullYear() === year;
    });
    
    if (hasTodo || hasSchedule) {
      return (
        <div className="calendar-badges">
          {hasTodo && <Badge status="warning" />}
          {hasSchedule && <Badge status="success" />}
        </div>
      );
    }
    
    return null;
  };
  
  // 渲染日程列表
  const renderScheduleList = () => {
    // 根据选中日期筛选日程
    const day = selectedDate.getDate();
    const month = selectedDate.getMonth();
    const year = selectedDate.getFullYear();
    
    const filteredSchedules = schedulesData.filter(schedule => {
      const scheduleDate = new Date(schedule.time.split(' ')[0]);
      return scheduleDate.getDate() === day && 
             scheduleDate.getMonth() === month && 
             scheduleDate.getFullYear() === year;
    });
    
    if (filteredSchedules.length === 0) {
      return <Empty description="当日无日程安排" />;
    }
    
    return (
      <List
        className="schedule-list"
        dataSource={filteredSchedules}
        renderItem={item => (
          <List.Item>
            <div className="schedule-item">
              <div className="schedule-type">
                <Tag color={item.type === '例会' ? 'blue' : item.type === '外勤' ? 'green' : 'purple'}>
                  {item.type}
                </Tag>
              </div>
              <div className="schedule-content">
                <div className="schedule-title">{item.title}</div>
                <div className="schedule-info">
                  <div className="schedule-time">{item.time.split(' ')[1]}</div>
                  <div className="schedule-location">{item.location}</div>
                </div>
                <div className="schedule-participants">
                  <TeamOutlined /> {item.participants.length}人参与
                </div>
              </div>
            </div>
          </List.Item>
        )}
      />
    );
  };
  
  return (
    <div className="workbench-container">
      <div className="workbench-header">
        <h1>个人工作台</h1>
      </div>
      
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Card className="workbench-card">
                <Statistic 
                  title="待办事项" 
                  value={todoListData.filter(todo => !todo.completed).length} 
                  suffix={`/ ${todoListData.length}`}
                />
                <Progress 
                  percent={Math.round((todoListData.filter(todo => todo.completed).length / todoListData.length) * 100)} 
                  size="small" 
                  status="active" 
                />
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card className="workbench-card">
                <Statistic 
                  title="未读消息" 
                  value={messageData.filter(msg => !msg.read).length} 
                  suffix={`/ ${messageData.length}`}
                />
                <Progress 
                  percent={Math.round((messageData.filter(msg => msg.read).length / messageData.length) * 100)} 
                  size="small" 
                  status="active" 
                />
              </Card>
            </Col>
          </Row>
          
          <Card 
            title="我的待办" 
            className="workbench-card todo-card"
            extra={<Button type="link" icon={<FilterOutlined />}>筛选</Button>}
          >
            <Tabs activeKey={todoActiveKey} onChange={setTodoActiveKey}>
              <TabPane tab="待处理" key="pending" />
              <TabPane tab="已完成" key="completed" />
              <TabPane tab="全部" key="all" />
            </Tabs>
            
            <List
              className="todo-list"
              itemLayout="horizontal"
              dataSource={getFilteredTodos()}
              renderItem={item => (
                <List.Item>
                  <div className="todo-item">
                    <div className="todo-checkbox">
                      {item.completed ? 
                        <CheckCircleOutlined className="todo-checked" /> : 
                        <ClockCircleOutlined className="todo-unchecked" />
                      }
                    </div>
                    <div className="todo-content">
                      <div className="todo-title">
                        {item.title}
                        {getPriorityTag(item.priority)}
                      </div>
                      <div className="todo-info">
                        <div className="todo-type">{item.type}</div>
                        <div className="todo-department">{item.department}</div>
                        <div className="todo-deadline">截止: {item.deadline}</div>
                      </div>
                    </div>
                    <div className="todo-actions">
                      <Dropdown overlay={
                        <Menu>
                          <Menu.Item key="1">完成</Menu.Item>
                          <Menu.Item key="2">详情</Menu.Item>
                          <Menu.Item key="3">转交</Menu.Item>
                          <Menu.Item key="4">延期</Menu.Item>
                        </Menu>
                      } placement="bottomRight">
                        <Button type="text" icon={<MoreOutlined />} />
                      </Dropdown>
                    </div>
                  </div>
                </List.Item>
              )}
            />
          </Card>
          
          <Card 
            title="我的消息" 
            className="workbench-card message-card"
            extra={<Input.Search placeholder="搜索消息" style={{ width: 200 }} />}
          >
            <List
              className="message-list"
              itemLayout="horizontal"
              dataSource={messageData}
              renderItem={item => (
                <List.Item>
                  <div className={`message-item ${!item.read ? 'message-unread' : ''}`}>
                    <div className="message-icon">
                      {item.read ? 
                        <Badge dot={false} /> :
                        <Badge status="processing" />
                      }
                      {item.type === 'notification' && <BellOutlined />}
                      {item.type === 'alert' && <BellOutlined />}
                      {item.type === 'reminder' && <ClockCircleOutlined />}
                      {item.type === 'announcement' && <SolutionOutlined />}
                      {item.type === 'system' && <SolutionOutlined />}
                    </div>
                    <div className="message-content">
                      <div className="message-header">
                        <div className="message-title">{item.title}</div>
                        {getMessageTypeTag(item.type)}
                      </div>
                      <div className="message-body">{item.content}</div>
                      <div className="message-footer">
                        <div className="message-sender">{item.sender}</div>
                        <div className="message-time">{item.time}</div>
                      </div>
                    </div>
                  </div>
                </List.Item>
              )}
            />
          </Card>
        </Col>
        
        <Col xs={24} lg={8}>
          <Card 
            title="日程安排" 
            className="workbench-card schedule-card"
            extra={<Button type="link" icon={<PlusOutlined />}>添加</Button>}
          >
            <div className="calendar-container">
              <Calendar 
                fullscreen={false} 
                dateCellRender={dateCellRender}
                onChange={value => setSelectedDate(value.toDate())}
              />
              <div className="calendar-legend">
                <div className="legend-item">
                  <Badge status="warning" />
                  <span>待办</span>
                </div>
                <div className="legend-item">
                  <Badge status="success" />
                  <span>日程</span>
                </div>
              </div>
            </div>
            
            <div className="schedule-container">
              <div className="schedule-header">
                <h3>{`${selectedDate.getFullYear()}年${selectedDate.getMonth() + 1}月${selectedDate.getDate()}日 日程`}</h3>
              </div>
              {renderScheduleList()}
            </div>
          </Card>
          
          <Card 
            title="我的收藏" 
            className="workbench-card collection-card"
            extra={<Input.Search placeholder="搜索收藏" style={{ width: 150 }} />}
          >
            <List
              className="collection-list"
              itemLayout="horizontal"
              dataSource={collectionsData}
              renderItem={item => (
                <List.Item>
                  <div className="collection-item">
                    <div className="collection-icon">
                      {getCollectionTypeIcon(item.type)}
                    </div>
                    <div className="collection-content">
                      <div className="collection-title">{item.title}</div>
                      <div className="collection-info">
                        <div className="collection-creator">{item.creator}</div>
                        <div className="collection-time">{item.createTime}</div>
                      </div>
                      <div className="collection-tags">
                        {item.tags.map(tag => (
                          <Tag key={tag}>{tag}</Tag>
                        ))}
                      </div>
                    </div>
                    <div className="collection-star">
                      {item.starred ? 
                        <StarFilled className="star-filled" /> : 
                        <StarOutlined />
                      }
                    </div>
                  </div>
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Workbench; 